'use client';

import { useState } from 'react';

import classNames from 'classnames';
import shuffle from 'lodash/shuffle';
import Image from 'next/image';
import Link from 'next/link';

import { BackgroundGridSVG } from '@/app/(homepage)/BackgroundGridSVG';
import randomizerPlaceholder from '@/app/(homepage)/sections/CategoryGridSection/randomizer-placeholder.jpg';
import { buttonClasses } from '@/components/Button';
import { topicPath } from '@/helpers/linkHelpers';
import ArrowRight from '@/icons/react/ArrowRight';
import ArrowsClockwise from '@/icons/react/ArrowsClockwise';
import Lightbulb from '@/icons/react/Lightbulb';
import { SiteNavMenuItemType } from '@/lib/firestore/dataFetchers/getSiteNavMenu';
import { getRandomItemsFromArray } from '@/lib/getRandomItemsFromArray';
import { COLORS } from '@/tokens';

interface TopicRandomizerModuleProps {
  topicPathsToRandomize: SiteNavMenuItemType[];
}

const backgroundGradients = [
  'from-cyan-200 to-magenta-200',
  'from-yellow-200 to-cyan-200',
  'from-magenta-100 to-cyan-300',
  'from-yellow-200 to-magenta-200',
  'from-yellow-100 to-yellow-400',
  'from-magenta-100 to-magenta-400',
  'from-cyan-100 to-yellow-400',
];

export function TopicRandomizerModule({
  topicPathsToRandomize,
}: TopicRandomizerModuleProps) {
  const [lastRandomTopic, setLastRandomTopic] =
    useState<SiteNavMenuItemType | null>(null);
  const [randomTopic, setRandomTopic] = useState<SiteNavMenuItemType | null>();
  const [isShuffling, setIsShuffling] = useState(false);
  const [backgroundGradient, setBackgroundGradient] = useState<string>(
    backgroundGradients[1],
  );

  function getBackgroundGradient() {
    const newGradient = shuffle(backgroundGradients)[0];
    const newIndex = backgroundGradients.indexOf(newGradient);

    if (newGradient === backgroundGradient) {
      setBackgroundGradient(backgroundGradients[newIndex + 1]);
    }

    setBackgroundGradient(newGradient);
  }

  function handleShuffle() {
    setIsShuffling(true);

    let randomPick = getRandomItemsFromArray(topicPathsToRandomize, 1)[0];

    if (randomPick && lastRandomTopic && randomPick.id === lastRandomTopic.id) {
      const currentIndex = topicPathsToRandomize.findIndex(
        (topic) => topic.id === lastRandomTopic.id,
      );

      let nextIndex = (currentIndex + 1) % topicPathsToRandomize.length;

      randomPick = topicPathsToRandomize[nextIndex];
    }

    setTimeout(() => {
      if (randomPick) {
        getBackgroundGradient();
        setRandomTopic(randomPick);
        setLastRandomTopic(randomPick);
      }

      setIsShuffling(false);
    }, 350);
  }

  return (
    <div className="@container w-full h-full">
      <div
        className={classNames(
          'w-full h-full flex flex-col gap-8 items-center justify-center',
          '@[80rem]:flex-row',
          'border border-primary rounded-3 shadow-sm p-6 w-full h-full overflow-hidden',
        )}
      >
        <div className="flex flex-col gap-3 items-center relative z-2">
          <div className="bg-yellow-400 rounded-full w-12 h-12 flex items-center justify-center">
            <Lightbulb className="w-5 h-5" />
          </div>

          <div className="text-center flex flex-col items-center gap-3">
            <h1 className="text-display-1000 font-serif">
              Not sure what to read next?
            </h1>
            <p className="text-body-450">
              Learn something new &mdash; Just a click away.
            </p>
          </div>

          <button
            onClick={handleShuffle}
            className={buttonClasses({
              variant: 'solid',
              color: 'black',
              size: 'lg',
            })}
          >
            <p className="text-heading-400">Shuffle</p>
            <ArrowsClockwise
              className={classNames('relative top-px', {
                'animate-spin': isShuffling,
                'group-hover:rotate-12 transition-transform': !isShuffling,
              })}
            />
          </button>
        </div>

        <div className="relative z-1">
          <div className="relative aspect-square z-2" style={{ width: 256 }}>
            {randomTopic ? (
              <Link
                href={topicPath(randomTopic.slug)}
                className={classNames(
                  'bg-gradient-to-br transition-all',
                  backgroundGradient,
                  'hover:scale-101 hover:shadow-lg',
                  'flex w-full h-full flex-col gap-3 items-center justify-center text-center rounded-3 shadow-md p-6',
                )}
              >
                <span className="text-heading-600">{randomTopic.title}</span>
                <span className="text-heading-350 flex items-center gap-2">
                  Explore
                  <ArrowRight />
                </span>
              </Link>
            ) : (
              <Image
                src={randomizerPlaceholder}
                alt=""
                className="block w-full h-full"
              />
            )}
          </div>

          {/* BACKGROUND ART "FRAME" */}
          <div
            className="absolute z-1 pointer-events-none"
            style={{ width: 1248, height: 1120, top: -608, left: -800 }}
          >
            <BackgroundGridSVG
              id="topic-shuffle-module"
              className="absolute inset-0"
              width={1248}
              height={1120}
            />
            <svg
              width="544"
              height="672"
              viewBox="0 0 544 672"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="absolute"
              style={{ left: 704, top: 192 }}
            >
              <path
                d="M512 640H480C462.327 640 448 625.673 448 608V576C448 558.327 433.673 544 416 544H284.055C275.218 544 268.055 536.837 268.055 528V528C268.055 519.163 260.891 512 252.055 512H64C46.3269 512 32 497.673 32 480V352C32 334.327 46.3269 320 64 320H96C113.673 320 128 305.673 128 288V160C128 142.327 113.673 128 96 128V128C78.3269 128 64 113.673 64 96V32"
                stroke={COLORS.GLOBAL['gray-200']}
                strokeWidth="2"
              />
              <circle
                cx="512"
                cy="640"
                r="3"
                fill={COLORS.GLOBAL['paper']}
                stroke={COLORS.GLOBAL['gray-200']}
                strokeWidth="2"
              />
              <circle
                cx="64"
                cy="32"
                r="3"
                fill={COLORS.GLOBAL['paper']}
                stroke={COLORS.GLOBAL['gray-200']}
                strokeWidth="2"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
